import { View } from '@play-co/timestep-core/ui';
import { user } from 'src/StateObserver';
import { SearchInputField } from 'src/game/components/SearchInputField';
import Scroll from 'src/game/components/shared/Scroll';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { getBuruFriends } from 'src/replicant/friends/friends.getters';
import { sendAddFriendRequest } from '../friends.sequences';
import { searchNonFriendPlayers } from '../friends.utils';
import { FriendItemParams, FriendItem } from './FriendItem';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import { startInviteFriendsSequence } from 'src/game/social/social.sequence';

export class FriendSearchTab {
  private tab: View;
  private scroll: Scroll<FriendItemParams, FriendItem>;
  private message: LangBitmapFontTextView;
  private search: SearchInputField;
  private items: FriendItemParams[] = [];
  private inviteButton: ButtonScaleViewWithText;
  private searching = false;

  constructor(tab: View) {
    this.tab = tab;

    this.search = new SearchInputField({
      superview: tab,
      placeholder: i18n('friends.searchPlaceholder'),
      x: 0,
      y: 45,
      inputClass: 'popupFriendsSearch',
      width: tab.style.width,
      height: 64,
      allowClear: true,
      submitButton: true,
      onSubmit: async (text) => {
        if (this.searching) return; // Prevent multiple searches
        sounds.sfx.play('ok');
        this.searchPlayers(text);
      },
      onClear: () => {
        sounds.sfx.play('ok');
        this.clearSearch();
      },
    });
    this.search.inputField.attachToPopup('popupFriends');

    this.scroll = new Scroll({
      superview: tab,
      recycle: true,
      createItem: (superview: View, i: number, data: FriendItemParams) => {
        const item = new FriendItem({ superview });
        item.setup(this.getItemData(data));
        return item;
      },
      setData: (i: number, data: FriendItemParams, item: FriendItem) => {
        item.setup(this.getItemData(data));
      },
      showBg: true,
      rect: {
        x: 0,
        y: 80,
        width: tab.style.width,
        height: tab.style.height - 80,
      },
    });

    this.message = new LangBitmapFontTextView({
      superview: tab,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: tab.style.width,
      height: 200,
      color: '#AE7E4A',
      centerOnOrigin: true,
      x: tab.style.width / 2,
      y: tab.style.height / 2,
      localeText: () => i18n('friends.searchInstruction'),
    });

    this.inviteButton = new ButtonScaleViewWithText({
      superview: tab,
      ...uiConfig.buttons.primary,
      fontSize: 46,
      font: 'Body',
      x: this.message.style.x,
      y: this.message.style.y + 150,
      width: 259,
      height: 100,
      centerOnOrigin: true,
      visible: false,
      localeText: () => i18n('social.invite.generic.title'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await startInviteFriendsSequence(
          Feature.FRIENDS._,
          Feature.FRIENDS.SEARCH_PLAYERS,
        );
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CLOSE,
      },
    });
  }

  private getItemData(params: FriendItemParams) {
    const requestsIds = Object.keys(user().friendshipRequests);
    return {
      ...params,
      requestSent: requestsIds.includes(params.id),
      onAdd: async () => {
        const ok = await sendAddFriendRequest(
          {
            id: params.id,
            name: params.name,
            profilePicture: params.profilePicture,
            frame: params.frame,
            species: params.species,
          },
          { sourceFeature: 'search' },
        );
        if (ok) this.refresh();
      },
    };
  }

  public refresh() {
    const friendsIds = getBuruFriends(user());
    const filtered = this.items.filter((item) => {
      return !friendsIds.includes(item.id);
    });
    this.scroll.setItems(filtered);

    // If no results and no search, display the search instruction message
    if (!filtered.length && !this.search.inputField.text.length) {
      this.message.localeText = () => i18n('friends.searchInstruction');
    }

    // If no results and search, display the no results message
    if (!filtered.length && this.search.inputField.text.length) {
      this.message.localeText = () => i18n('friends.noResults');
      this.inviteButton.show();
    } else {
      this.inviteButton.hide();
    }

    this.message.updateOpts({
      visible: filtered.length === 0,
    });
  }

  private async searchPlayers(searchStr: string) {
    if (this.searching) return; // Prevent multiple searches
    this.searching = true;
    this.message.hide(); // Can show again on refresh, depending on results
    this.search.inputField.disableInteraction();
    this.items = await withLoading(
      () => searchNonFriendPlayers(searchStr).get(),
      {
        superview: this.tab,
        feature: Feature.FRIENDS._,
        subFeature: Feature.FRIENDS.SEARCH_PLAYERS,
      },
    );
    this.searching = false;
    this.search.inputField.enableInteraction();
    this.refresh();
  }

  private clearSearch() {
    this.search.inputField.text = '';
    this.items = [];
    this.refresh();
  }
}
